import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import LoginForm from '../../../components/authentication/LoginForm'
import {
  askNewShortToken,
  CardinalApiState,
  completeAuthentication,
  markNewShortTokenRequestAsDone,
  setEmail,
  setToken,
  setWaitingForToken,
  startAuthentication,
} from '../../../core/services/auth.api'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import logo from '../../../assets/logo_vertical.svg'
import '../index.less'
import { createSelector } from '@reduxjs/toolkit'
import { Solution } from '@icure/cardinal-sdk'
import { ModalRecoveryKeyRequest } from '../../../components/user/ModalRecoveryKeyRequest'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ModalChooseEnvironment } from '../../../components/authentication/ModalChooseEnvironment'
import { ModalParentAuth } from '../../../components/authentication/ModalParentAuth'
import { ModalParentRecoveryKey } from '../../../components/authentication/ModalParentRecoveryKey'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    waitingForToken: cardinalApi.waitingForToken,
    loginProcessStarted: cardinalApi.loginProcessStarted,
    recoveryKeyRequest: cardinalApi.recoveryKeyRequest,
    invalidToken: cardinalApi.invalidToken,
    invalidEmail: cardinalApi.invalidEmail,
    refusedEnvironmentSelection: cardinalApi.refusedEnvironmentSelection,
    newShortTokenIsRequested: cardinalApi.newShortTokenIsRequested,
    parentCredentialsRequest: cardinalApi.parentCredentialsRequest,
    parentRecoveryKey: cardinalApi.parentRecoveryKey,
  }),
)

export default function LoginPage() {
  const dispatch = useAppDispatch()
  const { waitingForToken, loginProcessStarted, recoveryKeyRequest, invalidToken, invalidEmail, refusedEnvironmentSelection, newShortTokenIsRequested, parentCredentialsRequest, parentRecoveryKey } =
    useAppSelector(reduxSelector)
  const { t } = useTranslation()

  const startAuthenticationProcessWithEmailAndCaptchaToken = (email: string, captchaToken: Solution) => {
    dispatch(setEmail({ email: email }))
    dispatch(startAuthentication({ captchaToken: captchaToken }))
  }
  const completeAuthenticationProcessWithEmailAndValidationCode = (validationCode: string) => {
    dispatch(setToken({ token: validationCode }))
    dispatch(completeAuthentication())
  }
  const startAuthenticationProcessWithNewCaptchaToken = (captchaToken: Solution) => {
    dispatch(startAuthentication({ captchaToken: captchaToken }))
    dispatch(markNewShortTokenRequestAsDone())
  }

  const requestNewShortToken = () => dispatch(askNewShortToken())

  useEffect(() => {
    return () => {
      dispatch(setWaitingForToken(false))
    }
  }, [])

  return (
    <div className="auth-page">
      <div className="auth-page__logo">
        <img src={logo} alt={t('logo.alt')} />
      </div>

      <LoginForm
        state={loginProcessStarted ? 'loading' : waitingForToken ? 'waitingForToken' : 'initialised'}
        submitEmailForTokenRequest={(email: string, captchaToken: Solution) => startAuthenticationProcessWithEmailAndCaptchaToken(email, captchaToken)}
        submitEmailAndValidationTokenForAuthentication={(validationCode: string) => completeAuthenticationProcessWithEmailAndValidationCode(validationCode)}
        requestNewShortToken={requestNewShortToken}
        startAuthenticationProcessWithNewCaptchaToken={(captchaToken: Solution) => startAuthenticationProcessWithNewCaptchaToken(captchaToken)}
        invalidToken={invalidToken}
        invalidEmail={invalidEmail}
        refusedEnvironmentSelection={refusedEnvironmentSelection}
        newShortTokenIsRequested={newShortTokenIsRequested}
      />
      {recoveryKeyRequest && createPortal(<ModalRecoveryKeyRequest />, document.body)}
      {parentCredentialsRequest && createPortal(<ModalParentAuth />, document.body)}
      {parentRecoveryKey && createPortal(<ModalParentRecoveryKey />, document.body)}
      <ModalChooseEnvironment />
    </div>
  )
}
